import React from 'react';

/**
 * THE sortable column header — one `<th>`, used by every table page.
 *
 * Product owner, 2026-08-08: *"breakdown needs the always show the up down
 * filter arrows for the table view. Please make the table view layout a single
 * piece of code or class that can be used on all table pages so we dont diverge
 * ever again"*.
 *
 * ══════════════════════════════════════════════════════════════════════
 * 🚨 WHAT THIS IS, AND — MORE IMPORTANTLY — WHAT IT IS NOT
 * ══════════════════════════════════════════════════════════════════════
 * **This is the HEADER CELL. It is not, and must never become, a shared
 * `<table>`.** His words invite the wider reading and the next person will try
 * it, so the reason is recorded here rather than left to be rediscovered:
 *
 *   - `components/OrderTableView.js` is **`table-fixed` with a `<colgroup>` of
 *     declared pixel widths**. That is load-bearing, not incidental: the widths
 *     are what let the collapsed item-preview's four grid tracks line up under
 *     Item / Quantity / Unit Price / Total Price, and `table-auto` would destroy
 *     that alignment. It is documented at length in CLAUDE.md's
 *     sanctioned-exception box.
 *   - `components/gags/GagGroupTable.js` is ALSO **`table-fixed` with a shared
 *     `<colgroup>`** since 2026-08-09, rendered as N grouped tables.
 *     ⚠️ **THIS BULLET SAID "auto-layout, 15 columns, no `<colgroup>`,
 *     `min-w-full`" UNTIL THE 2026-08-09 INTEGRATION MERGE, AND IT WAS A FALSE
 *     CLAIM THAT MERGED CLEAN** — `feat/breakdown-inline-row-edit` gave the
 *     Breakdown declared widths (`constants/gagTableColumns.js`) because a
 *     `<select>`/`<input>` carries its own intrinsic minimum width and controls
 *     in auto-layout cells resize the columns of whichever group holds the
 *     expanded row. It touched neither this file nor the two comments in
 *     `OrderTableView.js`, so three copies of the old sentence survived
 *     byte-identically on every side of the merge and no conflict was raised.
 *
 * **The two tables are STILL not unifiable into one `<table>`** — they differ in
 * column count (12 vs 15), in grouping (one table vs N) and in scroll model —
 * **but they no longer differ in LAYOUT MODEL, so do not repeat the old
 * argument.** It used to be "unifying them means either giving the Breakdown 15
 * measured pixel widths or taking the colgroup off the order table"; the
 * Breakdown now HAS its measured widths, so only the first half was ever the
 * real obstacle and it is gone. What genuinely IS the same on both — and what
 * had already begun to diverge, which is what he noticed — is the header cell:
 * the `<th>`, its button, its caret, and its `aria-sort`.
 *
 * 🔑 One consequence to carry, because it inverts a rule stated further down:
 * the order table's *"a sortable header must FIT its column"* measurement used
 * to have **no meaning** on the Breakdown (a header cannot overflow a width it
 * defines itself). From 2026-08-09 it applies to all 13 of the Breakdown's data
 * columns exactly as it does here.
 *
 * ══════════════════════════════════════════════════════════════════════
 * 🔑 EVERY DETAIL BELOW IS MEASURED BEHAVIOUR. NONE OF IT IS STYLE.
 * ══════════════════════════════════════════════════════════════════════
 * All of it was learned the expensive way on the order table and is carried here
 * intact. jsdom sees almost none of it, so it cannot be re-derived from a green
 * suite.
 *
 * 1. 🪤 **`p-0` ON THE `<th>` IS LOAD-BEARING.** The BROWSER default is
 *    `th { padding: 1px }` and Tailwind's preflight does not reset padding. The
 *    non-sortable branch overrides it with `px-2 py-2`; the sortable branch
 *    moves padding onto the inner button and therefore INHERITS the UA 1px —
 *    putting every sortable heading 9px from the cell edge while its own data
 *    cells (`px-2`) start at 8px. A 1px misalignment on every sortable column.
 *    It shipped once. jsdom applies no UA table stylesheet and cannot see it.
 *
 * 2. 📏 **The caret box is `w-3` (12px), because `▲`/`▼` MEASURE 10.94px** at
 *    `text-xs` in Inter (`↕` is 6.61px). It was `w-2.5` (10px) and the ACTIVE
 *    caret overflowed its own reserved box by 0.94px from the day the headers
 *    shipped. `text-center` keeps the narrow `↕` where the wide `▲` sits, so
 *    switching columns does not shuffle the glyph sideways.
 *
 * 3. **`shrink-0` on the caret span.** A flex item shrinks by default, so an
 *    over-long label would reclaim space from the caret — losing the only
 *    indicator of sort DIRECTION while still overflowing. Not hypothetical:
 *    "ORDER TOTAL" did out-grow its column.
 *
 * 4. 🚨 **The inactive caret's hover step is `opacity`, NOT a second colour, and
 *    that is a MEASURED CASCADE FACT.** `client/src/index.css` carries global
 *    dark-mode overrides — `html.dark .text-gray-400 { color: #6b7280
 *    !important }` and siblings — and an `!important` author declaration beats
 *    any non-important rule whatever its specificity. So
 *    `dark:group-hover:text-gray-300` is **inert**. Measured with the candidate
 *    rules injected (so the test was about the cascade, not about whether
 *    Tailwind had compiled them):
 *      colour hover  — light 2.43 → 4.63 ✅, **dark 3.03 → 3.03 ❌ nothing**
 *      opacity hover — light 1.80 → 2.43 ✅, dark 2.18 → 3.03 ✅
 *    A colour hover works on the maintainer's screen and dies silently in dark.
 *    🪤 The `dark:` variant is therefore *delivered by `index.css`*, not by
 *    Tailwind; it is kept so declared intent and enforced colour AGREE.
 *
 * 5. ♿ **`aria-sort` goes on the `<th>`, never on the inner button** — the ARIA
 *    `columnheader` role belongs to the cell and a screen reader announces the
 *    state from there. Inactive sortable columns get `'none'` rather than
 *    omitting it, so the set of sortable columns is discoverable without
 *    clicking one.
 *
 * 6. **A NON-SORTABLE column gets no button, no pointer cursor, no focus ring
 *    and no `aria-sort`** — nothing that offers an interaction it cannot honour.
 *    That branch is why `onSort` is optional rather than the caller rendering a
 *    different element: keeping both branches here is what stops them drifting.
 *
 * 7. **A real `<button>`, not a `<th onClick>`.** Keyboard operability and
 *    focusability come free, and the visible focus ring matters because
 *    `TH_BASE` is deliberately low-contrast.
 *
 * 8. 🔀 **`children` (2026-08-09) — a SIBLING of the button, never inside it,
 *    and that placement is the entire point of the prop.** The Gag Breakdown's
 *    column-resize handle has to live in the `<th>` so it can position against
 *    the cell's right edge, and it has to live OUTSIDE the button so a drag
 *    cannot fire a sort: the button is `w-full`, so anything inside it bubbles
 *    a pointerup straight into `handleClick`. A dedicated hit area that is not
 *    a descendant of the button separates the two gestures **structurally** —
 *    no movement threshold, no suppress-the-next-click flag, nothing to
 *    remember. See `components/ui/ColumnResizeHandle.js`.
 *    ⚠️ The `<th>` gains `relative` **only when children are present**, so a
 *    caller that passes none renders a byte-identical cell. Without it an
 *    absolutely-positioned child resolves against the nearest positioned
 *    ancestor — on the Breakdown that is the group card, and all fifteen
 *    handles pile up on its right edge.
 *    🟢 `components/OrderTableView.js` passes no children and is unaffected.
 *    Both branches of this component accept them, because a NON-sortable
 *    column is still a column a user may want to resize.
 *
 * ── Contrast, for the record ──────────────────────────────────────────
 * Against the header background (light `#f9fafb`; dark `#1e293b`, because
 * `html.dark .bg-gray-50` overrides a `dark:bg-gray-700/50` — measured):
 *   inactive caret  1.80 light / 2.18 dark at rest, 2.43 / 3.03 on hover
 *   active caret    3.52 / 5.75
 * The active one carries STATE and clears WCAG 1.4.11's 3:1 in both themes. The
 * inactive one is a REDUNDANT hint — the control's identity is carried by a real
 * button with a focus ring, a `title` and `aria-sort` — which is what makes its
 * lower figures acceptable. `opacity-70` is the dial if it needs to be stronger.
 */

/** The glyphs. Exported so tests and components share one definition. */
export const SORT_CARET_ASC = '▲';
export const SORT_CARET_DESC = '▼';
export const SORT_CARET_NONE = '↕';

/** See note 2 — 12px, because the widest glyph is 10.94px. */
export const SORT_CARET_BOX = 'w-3 shrink-0 text-center';

/** See the contrast note — blue, full strength, carries STATE. */
export const SORT_CARET_ACTIVE = 'text-blue-500 dark:text-blue-400';

/** See note 4 — opacity hover, never a colour hover. */
export const SORT_CARET_INACTIVE =
  'text-gray-400 dark:text-gray-500 opacity-70 group-hover:opacity-100';

/**
 * The shared heading type treatment. Both tables had this identical string.
 * ⚠️ It does NOT include padding — padding differs by branch (see note 1) and is
 * applied by this component, not by the caller.
 */
export const TH_BASE =
  'text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 whitespace-nowrap';

/**
 * @param {React.ReactNode} label     the heading text.
 * @param {string|null} [sortId]      this column's sort key. **A ROW KEY, not a
 *   column key** — see the caller-side warning below. `null`/absent makes the
 *   column non-sortable (note 6).
 * @param {string|null} [activeSort]  the sort key currently in effect.
 * @param {'asc'|'desc'} [sortDir]
 * @param {(sortId: string) => void} [onSort]  omit and the column is not
 *   sortable, whatever `sortId` says — a bare shell renders plain headings.
 * @param {'left'|'right'} [align]
 * @param {string} [className]        extra `<th>` classes (sticky treatment,
 *   per-surface background). Never padding.
 * @param {object} [style]            inline `<th>` style (the sticky `top`).
 * @param {string} [testId]           `data-testid` on the BUTTON.
 * @param {string} [ariaLabel]        for a heading with no visible text.
 * @param {string} [role]             explicit `<th>` role, OMITTED by default
 *   (no attribute). Only `OrderTableShell`'s split header passes it
 *   (`columnheader`, 2026-09-26): its header `<table>` is `role="none"`, which
 *   would otherwise strip the cell's native role — see that shell.
 * @param {React.ReactNode} [children]  extra cell content rendered AFTER the
 *   button — today, the Gag Breakdown's column-resize handle. See note 8.
 * @param {boolean} [truncateLabel]  OPT-IN, default `false` (2026-09-27, the
 *   Breakdown's `Fit columns to screen`): the LABEL ellipses when the column is
 *   narrower than it, the caret never does (it stays `shrink-0`). The full text
 *   stays reachable on hover through the text the cell already carries — the
 *   sort button's `title` names the label, and a non-sortable heading gets the
 *   label as its span's `title`; no second tooltip is added to a sortable one.
 *   Omitted, every element and class is byte-identical to before (the order
 *   tables pass nothing). 🪤 `min-w-0` is load-bearing: a flex item's default
 *   `min-width: auto` would pin the label at its full width and never ellipse.
 * @param {string|null} [sortLabel]  OPT-IN, default `null` (2026-09-27, the
 *   Breakdown's D/N column): the NAME used inside the "Sort by …" sentence,
 *   in place of `label`, for a SINGLE-FIELD column only. `label` still renders
 *   in the `<th>` — this exists because a glyph short enough to fit a column
 *   ("D/N") is not a name a sentence can use ("Sort by D/N"), and the two
 *   questions ("what fits the cell" and "what names the column in prose") do
 *   not share one answer. Omitted, `nextField.sortLabel || nextField.label`
 *   resolves to `label`, so every existing caller (cycling columns included,
 *   none of which set it) renders the byte-identical string.
 *
 * 🪤 **`sortId` IS A ROW KEY AND IT IS NOT ALWAYS THE COLUMN KEY, and getting it
 * wrong fails SILENTLY.** Every sort reducer in this app ends in
 * `default: aVal = (a[sortField] || '').toString().toLowerCase()`, so a name no
 * row carries compares `undefined` with `undefined`, sorts nothing, and leaves a
 * header that looks completely live. The order table already has two that differ
 * (`ordered_by` → `requested_by`, `job_name` → `job_rig`). **Assert every
 * column's key against a real row rather than hand-checking them.**
 */
/**
 * ═══════════════════════════════════════════════════════════════════════
 * 🔀 CYCLING COLUMNS (2026-08-08) — a header may carry N sort FIELDS.
 * ═══════════════════════════════════════════════════════════════════════
 * Product owner, in three messages: *"clikcing on date sort in the orders list
 * could cycle orderd date, due date and slack… if we do this the title should
 * change from 'date' when we cycle to 'Due' and 'Slack'"*, *"I dont think we
 * need to ever order anything in revers so just the 3 states I think?"*, and
 * *"yes PO number and order ID is another great use case"*.
 *
 * 🔑 **WHY THIS IS SOUND WHERE THE EARLIER FOUR-STATE PO PROPOSAL WAS NOT.**
 * That proposal (PO asc → PO desc → order asc → order desc) was declined
 * because **a caret shows DIRECTION and cannot show WHICH FIELD** — clicks 1
 * and 3 would have rendered an identical header while sorting different things.
 * **A label that CHANGES removes that objection entirely.** It is also not a
 * hidden mode: these columns physically CONTAIN the values being cycled, so the
 * header names the line of the cell you are sorting by.
 *
 * ── The contract ──────────────────────────────────────────────────────
 * `fields` is an ordered list of `{ sortField, label, direction, description? }`.
 * Each click advances to the next and WRAPS. **Direction is FIXED per field and
 * never reverses** — his explicit ruling — so each entry declares the one
 * direction that is useful for it. The caret still SHOWS the direction; it is no
 * longer a control.
 *
 * 🚨 **A SINGLE-FIELD COLUMN IS BYTE-FOR-BYTE UNCHANGED, INCLUDING ITS
 * DIRECTION TOGGLE.** With one field the component calls `onSort(sortField)`
 * with NO second argument, exactly as before, and the page's existing
 * same-field-toggles-direction logic runs untouched. That matters most for the
 * **Gag Breakdown**, which is the bigger consumer of this component and has no
 * cycling columns at all. Pinned by its own tests.
 *
 * ♿ **The changing label IS the accessible name** — it is the button's text
 * content, so three presses announce three different names. **Do not add a
 * static `aria-label` to a cycling column**: it would override the text and
 * become a lie the moment the label changed. `ariaLabel` exists only for
 * headings with no visible text (the expand chevron).
 *
 * 💡 **`title` SAYS WHAT THE COLUMN IS SHOWING *AND* WHERE THE NEXT CLICK GOES —
 * 2026-09-12. It used to name the next field ALONE.** Owner: *"update the tool
 * tip on the date, due, slack column header to tell us what each one shows us"*.
 * A cycling header names a DIFFERENT quantity in every state, and the label is
 * one word, so "Due" and "Slack" were unexplained anywhere in the product. Any
 * `fields` entry MAY carry a **`description`** — one or two short sentences
 * about what that state SHOWS — and when the field CURRENTLY in effect has one
 * the title becomes `<description> Click to sort by <next label>.`
 *
 * 🔑 **"Currently in effect" is the SAME resolution `shownLabel` uses** — the
 * active field when this column is the sort, otherwise the FIRST — and it is
 * read from one `currentField` const below rather than re-derived, so the words
 * in the tooltip can never describe a state other than the one the label names.
 * 🪤 At rest the current field and the NEXT field are both `fields[0]`, so the
 * Date column reads *"Date the order was raised. Click to sort by Date."* That
 * is correct, not a bug: the resting column both SHOWS the order date and sorts
 * by it on the first click.
 *
 * 🚨 **NO `description` ⇒ BYTE-IDENTICAL TO THE OLD STRING**, `Sort by <next
 * label>` — so every single-field column (the whole Gag Breakdown) and the PO
 * cycle, which carry none, are unchanged. Adding one to the PO column is a
 * DATA-only change: one key in `ORDER_TABLE_COLUMNS`, no edit here.
 */
export default function SortableHeader({
  label,
  sortId = null,
  fields = null,
  activeSort = null,
  sortDir = 'asc',
  onSort = null,
  align = 'left',
  className = '',
  style,
  testId,
  ariaLabel,
  role,
  children = null,
  truncateLabel = false,
  sortLabel = null,
}) {
  const alignClass = align === 'right' ? 'text-right' : 'text-left';
  // Note 8 — only when there is something to position, so a caller that passes
  // no children gets exactly the cell it got before this prop existed.
  const positionClass = children ? 'relative' : '';

  // One column, one ordered field list. A legacy `sortId`/`label` pair is just
  // the single-entry case, so there is ONE code path below rather than two.
  // `sortLabel` rides on that single synthesized entry only — a caller using
  // `fields` (a cycling column) states each field's own prose name inline.
  const fieldList = fields && fields.length
    ? fields
    : (sortId ? [{ sortField: sortId, label, sortLabel }] : []);
  const cycles = fieldList.length > 1;
  const sortable = fieldList.length > 0 && typeof onSort === 'function';

  // Note 6 — the non-sortable branch. Plain text in a plain `<th>`; this is also
  // the branch every column takes when a caller passes no `onSort` at all.
  if (!sortable) {
    return (
      <th
        scope="col"
        role={role}
        className={`${positionClass} px-2 py-2 ${TH_BASE} ${alignClass} ${className}`.replace(/\s+/g, ' ').trim()}
        style={style}
        aria-label={ariaLabel}
      >
        {truncateLabel && label
          ? <span className="block truncate" title={typeof label === 'string' ? label : undefined}>{label}</span>
          : label}
        {children}
      </th>
    );
  }

  // Which of THIS column's fields is the page currently sorted by, if any.
  const activeIndex = fieldList.findIndex((f) => f.sortField === activeSort);
  const active = activeIndex >= 0;
  // The field CURRENTLY IN EFFECT: the ACTIVE one when this column is the sort,
  // otherwise the column's FIRST — which is its resting identity ("Date", "PO").
  // 🔑 ONE resolution, read by BOTH the label and the tooltip. Deriving the
  // tooltip's idea of "current" separately is how a header comes to say "Slack"
  // while describing the order date.
  const currentField = fieldList[active ? activeIndex : 0];
  const shownLabel = currentField.label;
  // What the next click selects: the following field when active (wrapping), or
  // the first when this column is not the current sort.
  const nextField = fieldList[active ? (activeIndex + 1) % fieldList.length : 0];

  // 💡 See the docblock. The description (when the current field declares one)
  // says what the column SHOWS; the second sentence says where the click goes.
  // 🚨 The no-description branch is byte-identical to the string this component
  // has always produced — that is what keeps every single-field column, and the
  // PO cycle, unchanged.
  // 🔀 `sortLabel` (2026-09-27) — the PROSE name for this sentence, when it
  // differs from the `<th>`'s rendered glyph (D/N → "Day/Night"). Absent, this
  // is `nextField.label`, byte-identical to before.
  const nextSortName = nextField.sortLabel || nextField.label;
  const nextLabelIsText = typeof nextSortName === 'string';
  let title;
  if (!nextLabelIsText) title = 'Sort by this column';
  else if (currentField.description) {
    title = `${currentField.description} Click to sort by ${nextSortName}.`;
  } else title = `Sort by ${nextSortName}`;

  const handleClick = () => {
    // 🚨 A single-field column calls `onSort(field)` with NO direction, so the
    // page's existing toggle runs and nothing about its behaviour changes. Only
    // a cycling column passes an explicit direction, because its directions are
    // fixed and must not be toggled.
    if (cycles) onSort(nextField.sortField, nextField.direction);
    else onSort(nextField.sortField);
  };

  return (
    <th
      scope="col"
      role={role}
      // Note 5 — on the CELL, never the button. 'none' rather than omitted.
      // ♿ Tracks the ACTIVE FIELD, so a cycling column reports the direction of
      // whichever of its fields is in effect.
      aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
      // Note 1 — `p-0` is NOT redundant. The UA default is `th { padding: 1px }`.
      className={`${positionClass} p-0 ${TH_BASE} ${alignClass} ${className}`.replace(/\s+/g, ' ').trim()}
      style={style}
    >
      <button
        type="button"
        onClick={handleClick}
        // The caret alone is a shape/colour signal, so the control also says
        // what it does in words. 💡 Since 2026-09-12 it says BOTH what the
        // column is showing (the current field's `description`, when it has
        // one) and where the next click goes. With no description it is the
        // string it has always been. Built above — see the docblock.
        title={title}
        // ♿ Only for a heading with no visible text. A cycling column must NOT
        // pass this — it would override the changing label and become a lie.
        aria-label={ariaLabel}
        // 🔑 `group` exists ONLY so the inactive caret can step its opacity on
        // hover. The caret carries its own colour class and so does NOT inherit
        // this button's `hover:text-…` — the hover response has to be asked for
        // explicitly, and it has to be opacity (note 4).
        className={`group w-full flex items-center gap-1 px-2 py-2 ${TH_BASE} ${
          align === 'right' ? 'justify-end' : ''
        } hover:text-gray-700 dark:hover:text-gray-200 focus:outline-none focus:ring-1 focus:ring-blue-400 rounded ${
          active ? 'text-gray-700 dark:text-gray-200' : ''
        }`.replace(/\s+/g, ' ').trim()}
        data-testid={testId}
      >
        {/* ♿ THE ACCESSIBLE NAME. It changes with the active field, which is
            what makes three presses announce three different names. */}
        <span className={truncateLabel ? 'truncate min-w-0' : undefined}>{shownLabel}</span>
        {/* `aria-hidden` because `aria-sort` on the cell already carries this to
            assistive tech — announcing a "▲" glyph as well is noise. */}
        <span
          aria-hidden="true"
          className={`${SORT_CARET_BOX} ${active ? SORT_CARET_ACTIVE : SORT_CARET_INACTIVE}`}
        >
          {active ? (sortDir === 'asc' ? SORT_CARET_ASC : SORT_CARET_DESC) : SORT_CARET_NONE}
        </span>
      </button>
      {/* Note 8 — OUTSIDE the button. Moving this inside is how a drag starts
          firing sorts, and it will look like a one-line tidy-up. */}
      {children}
    </th>
  );
}
